import { Modal } from 'reactstrap';
import styled from 'styled-components';

import { useAppDispatch } from 'hooks/redux';
import { Button } from 'modules/ScrShopNew/components/Buttons';
import { Input } from 'modules/ScrShopNew/components/Forms';
import { OTP_INPUT_PROPS, useOtpLoginFlow } from 'modules/ScrShopNew/hooks/useOtpLoginFlow';
import { applyCheckoutAfterSignIn } from 'modules/ScrShopNew/store/slices/shipping/actions';
import { StyledCloseIcon, StyledModalHeader } from 'modules/ScrShopNew/styles/modalDetails';

const Form = styled.form`
  margin: 0;
  padding: 0 30px 20px 30px;
  font-family: 'Noto Sans', sans-serif;
`;

const Text = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.5;
`;

// same red as the shop form field errors (Forms/FormPart)
const Notice = styled(Text)`
  color: #e50606;
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 12px;
  margin-top: 4px;
`;

const LinkButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font-size: 14px;
  color: #9b9a9a;
  cursor: pointer;
  text-decoration: underline;
`;

const SubmitButton = styled(Button)`
  margin-top: 20px;
  letter-spacing: 0.64px;
`;

// One-time-code sign-in / registration in the shop's black and white look. Steps, thunks and
// requests are the gallery's (useOtpLoginFlow); signing in also moves the checkout on.
export const ShopOtpLoginModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    isOpen,
    close,
    step,
    title,
    description,
    notice,
    submitLabel,
    submitDisabled,
    links,
    onSubmit,
    field
  } = useOtpLoginFlow({
    onSignedIn: ({ endCustomer }) => dispatch(applyCheckoutAfterSignIn(endCustomer))
  });

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={close}
      className="modal-dialog-centered"
      zIndex={99999}
      data-cy="otp-login-modal"
    >
      <StyledModalHeader>
        {title}
        <StyledCloseIcon onClick={close} data-cy="otp-login-modal-close" />
      </StyledModalHeader>
      <Form onSubmit={onSubmit} noValidate>
        {description && <Text data-cy="otp-login-modal-description">{description}</Text>}
        {field && (
          <Input
            // remount per step so the browser doesn't carry autofill over between screens
            key={step}
            id={`otpLogin-${field.kind}`}
            name={field.kind}
            label={field.label}
            placeholder={field.placeholder}
            value={field.value}
            onChange={(e) => field.onChange(e.currentTarget.value)}
            errorText={field.errorText}
            data-cy={`otp-login-${field.kind}`}
            {...(OTP_INPUT_PROPS[field.kind] as { type: string })}
          />
        )}
        {notice && <Notice data-cy="otp-login-modal-notice">{notice}</Notice>}
        {links.length > 0 && (
          <Links>
            {links.map((link) => (
              <LinkButton
                key={link.dataCy}
                type="button"
                onClick={link.onClick}
                data-cy={link.dataCy}
              >
                {link.label}
              </LinkButton>
            ))}
          </Links>
        )}
        <SubmitButton
          type="submit"
          className="sb-btn w-100"
          disabled={submitDisabled}
          data-cy="otp-login-modal-submit"
        >
          {String(submitLabel).toUpperCase()}
        </SubmitButton>
      </Form>
    </Modal>
  );
};
